import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    error: null,
    errorInfo: null,
  };

  public static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('Uncaught React Error:', error, errorInfo);
    console.error('COMPONENT STACK:\n', errorInfo.componentStack);
    this.setState({ errorInfo });
  }

  private handleReset = () => {
    this.setState({ hasError: false, error: null, errorInfo: null });
    window.location.href = '/';
  };

  public render() {
    if (this.state.hasError) {
      return (
        <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center p-6 font-mono">
          <div className="max-w-xl w-full bg-slate-900 border-2 border-red-500/50 rounded-2xl p-8 shadow-2xl space-y-6 animate-fade-in">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-red-500/20 border border-red-500/40 rounded-xl text-red-400">
                <AlertTriangle className="w-8 h-8 animate-pulse" />
              </div>
              <div>
                <h1 className="text-lg font-extrabold uppercase text-red-200 tracking-wide">
                  ⚠️ СТАЛАСЯ ПОМИЛКА РЕНДЕРИНГУ ІНТЕРФЕЙСУ
                </h1>
                <p className="text-xs text-slate-400 mt-1">
                  Система запобігла появі білого екрана.
                </p>
              </div>
            </div>

            <div className="bg-slate-950 border border-slate-800 rounded-xl p-4 space-y-2 text-xs">
              <div className="font-bold text-red-400">Деталі помилки:</div>
              <p className="text-slate-300 break-words font-mono">
                {this.state.error?.message || 'Невідома помилка виконання React.'}
              </p>
              {(this.state.error?.message?.includes('removeChild') || 
                this.state.error?.message?.includes('insertBefore') || 
                this.state.error?.message?.includes('не є дочірнім') ||
                this.state.error?.message?.includes('не является дочерним')) && (
                <div className="mt-3 p-3 bg-amber-500/10 border border-amber-500/30 rounded-lg text-amber-300 text-[11px] leading-relaxed">
                  💡 <strong>Порада:</strong> Ця помилка виникає, коли вбудований перекладач браузера (Google Translate) втручається в роботу сторінки. Платформа вже містить нативну підтримку мов у верхньому меню — будь ласка, вимкніть автопереклад браузера для цього сайту або натисніть «Оновити сторінку».
                </div>
              )}
            </div>

            <div className="flex items-center gap-3 pt-2">
              <button
                onClick={() => window.location.reload()}
                className="px-5 py-3 bg-blue-600 hover:bg-blue-500 text-white font-extrabold text-xs uppercase rounded-xl transition-all shadow-lg flex items-center gap-2 cursor-pointer"
              >
                <RefreshCw className="w-4 h-4" />
                <span>Оновити сторінку</span>
              </button>

              <button
                onClick={this.handleReset}
                className="px-5 py-3 bg-slate-800 hover:bg-slate-700 text-slate-200 font-extrabold text-xs uppercase rounded-xl transition-all border border-slate-700 flex items-center gap-2 cursor-pointer"
              >
                <Home className="w-4 h-4" />
                <span>На головну</span>
              </button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
